import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The reference phylogeny, drawn as a collapsible cladogram.
 *
 * `reference` is the document backend/phylogeny/opentree.py builds (served by
 * GET /phylogeny/reference): a tree of the reference-bank genera from the Open
 * Tree of Life synthesis, with its caveats and every supporting study.
 * `heldGenera` maps lower-case genus -> how many records the bank holds.
 *
 * Topology only -- the synthesis has no branch lengths -- so the drawing is an
 * indented cladogram, not a scaled phylogram, and it says so rather than
 * implying distances it does not have.
 *
 * Searching expands the path to every matching genus and marks it; a genus
 * the synthesis finds non-monophyletic is flagged where Open Tree placed it.
 */
const INITIAL_DEPTH = 2;

export function pathKey(path) {
  return path.join('.');
}

/** Paths of every node whose name contains `query`, plus all their ancestors. */
export function matchPaths(node, query) {
  const q = (query || '').trim().toLowerCase();
  const matches = new Set();
  const open = new Set();
  if (!q) return { matches, open };
  const walk = (n, path) => {
    const names = [n.name, n.requested, ...(n.broken_taxa || [])].filter(Boolean).map(s => s.toLowerCase());
    let hit = names.some(s => s.includes(q));
    (n.children || []).forEach((child, i) => {
      if (walk(child, [...path, i])) hit = true;
    });
    if (names.some(s => s.includes(q))) matches.add(pathKey(path));
    if (hit) open.add(pathKey(path));
    return hit;
  };
  walk(node, [0]);
  return { matches, open };
}

function allInternalPaths(node, path = [0], out = []) {
  if ((node.children || []).length) {
    out.push(pathKey(path));
    node.children.forEach((c, i) => allInternalPaths(c, [...path, i], out));
  }
  return out;
}

export default function PhylogenyTree({ reference, heldGenera = {}, initialQuery = '' }) {
  const { t } = useTranslation('phylogeny');
  // A caller may open the tree on a genus (the hub's DNA peek): the search is
  // pre-filled, so the path to it is expanded and it is marked, as a typed
  // search would.
  const [query, setQuery] = useState(initialQuery || '');
  const [expanded, setExpanded] = useState(() => {
    const open = new Set();
    const walk = (n, path) => {
      if (path.length <= INITIAL_DEPTH && (n.children || []).length) open.add(pathKey(path));
      (n.children || []).forEach((c, i) => walk(c, [...path, i]));
    };
    if (reference?.tree) walk(reference.tree, [0]);
    return open;
  });

  const tree = reference?.tree;
  const search = useMemo(() => (tree ? matchPaths(tree, query) : { matches: new Set(), open: new Set() }), [tree, query]);
  if (!tree) return null;

  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const isOpen = (key) => (query.trim() ? search.open.has(key) || expanded.has(key) : expanded.has(key));
  const taxa = reference.taxa || {};
  const studies = reference.supporting_studies || [];

  const downloadNewick = () => {
    const blob = new Blob([reference.newick || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pitriot-reference-phylogeny-${reference.source?.synth_id || 'opentree'}.tre`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const renderNode = (node, path) => {
    const key = pathKey(path);
    const children = node.children || [];
    const leaf = children.length === 0;
    const genus = (node.requested || node.name || '').split(' ')[0].toLowerCase();
    const held = leaf ? heldGenera[genus] : undefined;
    const matched = search.matches.has(key);
    const label = node.name || node.requested;
    return (
      <li key={key} role="treeitem" aria-expanded={leaf ? undefined : isOpen(key)} style={itemStyle}>
        {/* data-matched states the fact; the colour merely shows it. A theme
            token cannot be read back from the DOM under jsdom, so without
            this the "search marks the match" rule has nothing holding it. */}
        <div style={{ ...rowStyle, background: matched ? 'var(--c-warning-soft-7)' : undefined }}
             data-matched={matched ? 'true' : undefined}
             data-testid={leaf ? 'tree-tip' : 'tree-clade'}>
          {leaf ? <span style={twigStyle}>─</span> : (
            <button type="button" onClick={() => toggle(key)} style={toggleStyle} title={t('common:tip.treeToggle')}
                    aria-label={`${isOpen(key) ? t('collapse') : t('expand')} ${label || ''}`}>
              {isOpen(key) ? '▾' : '▸'}
            </button>
          )}
          {label
            ? <span dir="ltr" style={leaf ? tipName : cladeName}>{label}</span>
            : <span style={anonName}>{t('unnamedClade')}</span>}
          {!leaf && <span style={countStyle}>{t('tips', { count: node.tips || 0 })}</span>}
          {held ? <span style={heldStyle} title={t('heldLegend')}>{t('held', { count: held })}</span> : null}
          {node.broken_taxa?.length ? (
            <span style={brokenStyle} title={t('broken', { names: node.broken_taxa.join(', ') })}
                  data-testid="tree-broken">⚠ {node.broken_taxa.join(', ')}</span>
          ) : null}
        </div>
        {!leaf && isOpen(key) && (
          <ul role="group" style={groupStyle}>
            {children.map((c, i) => renderNode(c, [...path, i]))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div data-testid="phylogeny-tree">
      <p style={metaStyle}>
        {t('source', { synth: reference.source?.synth_id || '?', taxonomy: reference.source?.taxonomy_version || '?' })}
        {' · '}
        {t('stats', {
          placed: taxa.placed ?? 0, requested: taxa.requested ?? 0,
          unmatched: (taxa.unmatched || []).length, broken: (taxa.broken || []).length,
        })}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '6px 0 10px' }}>
        <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('search')}
               aria-label={t('search')} title={t('common:tip.treeSearch')} style={inputStyle} dir="ltr" />
        <button type="button" onClick={() => setExpanded(new Set(allInternalPaths(tree)))} title={t('common:tip.treeExpandAll')} style={smallButton}>{t('expandAll')}</button>
        <button type="button" onClick={() => setExpanded(new Set())} title={t('common:tip.treeCollapseAll')} style={smallButton}>{t('collapseAll')}</button>
        <button type="button" onClick={downloadNewick} title={t('common:tip.treeDownload')} style={smallButton}>⬇ {t('downloadNewick')}</button>
      </div>
      {query.trim() && search.matches.size === 0 && <p style={metaStyle}>{t('noMatch')}</p>}
      <p style={{ ...metaStyle, margin: '0 0 6px' }}><span style={heldStyle}>{t('held', { count: 'n' })}</span> {t('heldLegend')}</p>
      <ul role="tree" aria-label={t('title')} style={{ ...groupStyle, paddingInlineStart: 0, maxHeight: 520, overflow: 'auto' }}>
        {renderNode(tree, [0])}
      </ul>

      <details style={detailsStyle}>
        <summary style={summaryStyle}>{t('about')}</summary>
        <p style={metaStyle}>{reference.source?.method}</p>
        <strong style={{ fontSize: 13 }}>{t('caveatsTitle')}</strong>
        <ul style={{ margin: '4px 0 8px', fontSize: 13 }}>
          {(reference.caveats || []).map(c => <li key={c}>{c}</li>)}
        </ul>
        {(taxa.unmatched || []).length > 0 && (
          <p style={metaStyle}>{t('unmatched', { names: taxa.unmatched.join(', ') })}</p>
        )}
        <strong style={{ fontSize: 13 }}>{t('dataRights')}</strong>
        <p style={metaStyle}>{reference.source?.data_rights}</p>
      </details>

      <details style={detailsStyle}>
        <summary style={summaryStyle}>{t('studies', { count: studies.length })}</summary>
        <ol data-testid="tree-studies" style={{ margin: '6px 0', fontSize: 12, paddingInlineStart: 22 }}>
          {studies.map(s => (
            <li key={s.study_id} style={{ marginBottom: 4 }} dir="ltr">
              {s.reference || s.study_id}
              {s.doi_url && <> — <a href={s.doi_url} target="_blank" rel="noopener noreferrer">{t('doi')}</a></>}
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}

const metaStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '2px 0' };
const inputStyle = { padding: '5px 9px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 13, minWidth: 200 };
const smallButton = { background: 'var(--c-bg-muted)', border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 };
const groupStyle = { listStyle: 'none', margin: 0, paddingInlineStart: 18, borderInlineStart: '1px dashed var(--c-border-strong)' };
const itemStyle = { margin: 0 };
const rowStyle = { display: 'flex', alignItems: 'center', gap: 6, padding: '1px 4px', borderRadius: 4, fontSize: 13, lineHeight: 1.5 };
const toggleStyle = { background: 'none', border: 'none', cursor: 'pointer', width: 18, padding: 0, color: 'var(--c-text-body-fg)' };
const twigStyle = { width: 18, textAlign: 'center', color: 'var(--c-text-faint-fg)' };
const tipName = { fontStyle: 'italic', color: 'var(--c-text-fg)' };
const cladeName = { fontWeight: 600, color: 'var(--c-primary-fg)' };
const anonName = { color: 'var(--c-text-faint-fg)', fontSize: 12 };
const countStyle = { fontSize: 11, color: 'var(--c-text-muted-fg)' };
const heldStyle = { fontSize: 11, background: 'var(--c-primary-soft-6)', color: 'var(--c-success-2-fg)', borderRadius: 8, padding: '0 6px' };
const brokenStyle = { fontSize: 11, background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)', borderRadius: 8, padding: '0 6px' };
const detailsStyle = { marginTop: 10, border: '1px solid var(--c-border)', borderRadius: 6, padding: '6px 10px' };
const summaryStyle = { cursor: 'pointer', fontSize: 13, fontWeight: 600 };
